import { Plus, X } from 'lucide-react';
import { type Rule, type SubGroup, type FieldDef, makeRule, STANDARD_FIELDS } from './dsl-utils.ts';
import { RuleRow } from './RuleRow.tsx';

// ─── Logic toggle (AND / OR pill) ─────────────────────────────────────────────

function LogicToggle({
  value,
  onChange,
}: {
  value: 'and' | 'or';
  onChange: (v: 'and' | 'or') => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden text-xs font-medium shrink-0">
      {(['and', 'or'] as const).map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={`px-2.5 py-1 transition-colors uppercase tracking-wide ${
            value === v
              ? 'bg-zinc-900 text-white'
              : 'bg-white text-zinc-400 hover:text-zinc-700 hover:bg-zinc-50'
          }`}
        >
          {v}
        </button>
      ))}
    </div>
  );
}

// ─── RuleGroup ────────────────────────────────────────────────────────────────

interface RootGroupProps {
  logic: 'and' | 'or';
  rules: Rule[];
  fields: FieldDef[];
  onLogicChange: (v: 'and' | 'or') => void;
  onRulesChange: (rules: Rule[]) => void;
  /** When provided, a remove button is shown (used for sub-groups) */
  onRemove?: () => void;
  isRoot?: boolean;
}

export function RuleGroup({
  logic,
  rules,
  fields,
  onLogicChange,
  onRulesChange,
  onRemove,
  isRoot = false,
}: RootGroupProps) {
  const defaultField = fields[0]?.key ?? STANDARD_FIELDS[0].key;
  const defaultFieldType = fields[0]?.fieldType ?? 'text';

  const addRule = () => {
    onRulesChange([...rules, makeRule(defaultField, defaultFieldType)]);
  };

  const updateRule = (idx: number, rule: Rule) => {
    const next = [...rules];
    next[idx] = rule;
    onRulesChange(next);
  };

  const removeRule = (idx: number) => {
    onRulesChange(rules.filter((_, i) => i !== idx));
  };

  return (
    <div
      className={
        isRoot ? 'space-y-2' : 'border border-zinc-200 rounded-xl bg-zinc-50/60 p-3 space-y-2'
      }
    >
      {/* Header row */}
      <div className="flex items-center gap-2">
        {/* Label */}
        {isRoot ? (
          <span className="text-xs text-zinc-500 shrink-0">Match customers where</span>
        ) : (
          <span className="text-xs text-zinc-400 shrink-0">Any/all of:</span>
        )}

        <LogicToggle value={logic} onChange={onLogicChange} />

        {isRoot && (
          <span className="text-xs text-zinc-500 shrink-0">of these conditions apply</span>
        )}

        {/* Remove sub-group button */}
        {!isRoot && onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="ml-auto p-1 text-zinc-300 hover:text-red-400 hover:bg-red-50 rounded transition-colors"
            title="Remove group"
          >
            <X size={13} />
          </button>
        )}
      </div>

      {/* Rules */}
      {rules.length > 0 ? (
        <div className="space-y-2">
          {rules.map((rule, idx) => (
            <RuleRow
              key={rule.id}
              rule={rule}
              fields={fields}
              onChange={(r) => updateRule(idx, r)}
              onRemove={() => removeRule(idx)}
            />
          ))}
        </div>
      ) : (
        <p className="text-xs text-zinc-300 italic py-1">No conditions — add one below</p>
      )}

      {/* Add condition */}
      <button
        type="button"
        onClick={addRule}
        className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-700 transition-colors py-0.5"
      >
        <Plus size={12} />
        Add condition
      </button>
    </div>
  );
}

// ─── Sub-group wrapper ────────────────────────────────────────────────────────

export function SubGroupComponent({
  group,
  fields,
  onChange,
  onRemove,
}: {
  group: SubGroup;
  fields: FieldDef[];
  onChange: (g: SubGroup) => void;
  onRemove: () => void;
}) {
  return (
    <RuleGroup
      logic={group.logic}
      rules={group.rules}
      fields={fields}
      onLogicChange={(v) => onChange({ ...group, logic: v })}
      onRulesChange={(rules) => onChange({ ...group, rules })}
      onRemove={onRemove}
      isRoot={false}
    />
  );
}
